import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import config, { buildCfpSessionDocumentUrl, validateCfpSessionPdfFile } from '../../../config';
import {
  createUserCfpSessionDocument,
  deleteUserCfpSessionDocument,
  fetchAdminUserCfpSessionDocuments,
  fetchUserCfpSessionDocuments,
  updateUserCfpSessionDocument,
} from '../../../utils/api';
import { SuccessToast, useSuccessToast } from '../../shared/SuccessToast';
import { useContentCatalog } from '../../../utils/contentCatalog';
import styles from './ScheduleCfpSessionPage.module.css';
import {
  canManageCfpSessionDocuments,
  getCfpSessionDocumentsDisabledReason,
} from './cfpSessionStatus';

const dialogEase = [0.16, 1, 0.3, 1];

const overlayMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.22, ease: dialogEase },
};

const cardMotion = {
  initial: { opacity: 0, y: 20, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 12, scale: 0.97 },
  transition: { duration: 0.32, ease: dialogEase },
};

const sectionMotion = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.22, ease: dialogEase },
};


function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.89 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.42L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2 5 5h-5V4zM8 13h8v2H8v-2zm0 4h8v2H8v-2z" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="42"
        strokeDashoffset="12"
      />
    </svg>
  );
}

function ErrorCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
    </svg>
  );
}

function DocumentsFeedbackBanner({ feedback, onDismiss }) {
  if (!feedback || feedback.type === 'success') return null;

  const Icon = feedback.type === 'loading'
    ? SpinnerIcon
    : ErrorCircleIcon;

  return (
    <motion.div
      className={styles.listDocumentsFeedbackWrap}
      role="presentation"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.24, ease: dialogEase }}
    >
      <div
        className={`${styles.listActionFeedback} ${styles[`listActionFeedback-${feedback.type}`]}`}
        role={feedback.type === 'loading' ? 'status' : 'alert'}
        aria-live="polite"
      >
        <span
          className={`${styles.listActionFeedbackIcon} ${feedback.type === 'loading' ? styles.listActionFeedbackIconSpin : ''}`}
          aria-hidden="true"
        >
          <Icon />
        </span>
        <p className={styles.listActionFeedbackText}>{feedback.message}</p>
        {feedback.type !== 'loading' && (
          <button
            type="button"
            className={styles.listActionFeedbackDismiss}
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </motion.div>
  );
}

function DeleteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M6 7h12v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7zm3-5h6l1 1h4v2H4V3h4l1-1zm1 8h2v10h-2V10zm4 0h2v10h-2V10z" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    </svg>
  );
}

function PdfPlaceholderIllustration() {
  return (
    <svg viewBox="0 0 80 80" width="72" height="72" fill="none" aria-hidden="true">
      <rect x="10" y="18" width="38" height="50" rx="4" fill="rgba(4,71,102,0.05)" stroke="rgba(4,71,102,0.15)" strokeWidth="1.5" />
      <rect x="18" y="10" width="38" height="50" rx="4" fill="rgba(4,123,178,0.06)" stroke="rgba(4,123,178,0.18)" strokeWidth="1.5" />
      <rect x="26" y="28" width="14" height="2.5" rx="1.25" fill="rgba(4,71,102,0.22)" />
      <rect x="26" y="35" width="22" height="2.5" rx="1.25" fill="rgba(4,71,102,0.15)" />
      <rect x="26" y="42" width="18" height="2.5" rx="1.25" fill="rgba(4,71,102,0.15)" />
      <rect x="26" y="49" width="10" height="2.5" rx="1.25" fill="rgba(4,71,102,0.1)" />
    </svg>
  );
}


function CfpSessionDocumentsDialog({ session, navigate, isBusy, onClose, readOnly = false }) {
  const { catalog } = useContentCatalog();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [formError, setFormError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [editFileInputKey, setEditFileInputKey] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewStatus, setPreviewStatus] = useState('idle');
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null);
  const { message: successToastMessage, isExiting: successToastExiting, showToast: showSuccessToast } = useSuccessToast();

  const sessionId = session?.id;
  const sessionTopic = String(session?.topic ?? '').trim() || '—';
  const canManageDocuments = !readOnly && canManageCfpSessionDocuments(session);
  const documentsDisabledReason = !readOnly
    ? getCfpSessionDocumentsDisabledReason(session, catalog?.cfp_session_statuses)
    : null;

  const showFeedback = useCallback((nextFeedback) => {
    if (!nextFeedback) return;
    if (nextFeedback.type === 'success') {
      showSuccessToast(nextFeedback.message);
      return;
    }
    setFeedback(nextFeedback);
    setFormError('');
  }, [showSuccessToast]);

  const dismissFeedback = useCallback(() => {
    setFeedback(null);
  }, []);

  const loadDocuments = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    const fetchDocuments = readOnly
      ? fetchAdminUserCfpSessionDocuments
      : fetchUserCfpSessionDocuments;
    const result = await fetchDocuments({ cfpSessionId: sessionId }, navigate);
    if (result.ok) {
      setDocuments(Array.isArray(result.documents) ? result.documents : []);
    } else {
      showFeedback({ type: 'error', message: result.message || 'Failed to load documents.' });
    }
    setLoading(false);
  }, [navigate, readOnly, sessionId, showFeedback]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (canManageDocuments) return undefined;
    resetEditForm();
    setDeleteConfirmDoc(null);
    return undefined;
  }, [canManageDocuments]);

  // Auto-select first document with a PDF for preview
  useEffect(() => {
    if (previewDocId !== null) return;
    const first = documents.find((d) => buildCfpSessionDocumentUrl(d.document));
    if (first) setPreviewDocId(first.id);
  }, [documents, previewDocId]);

  useEffect(() => {
    const doc = documents.find((item) => item.id === previewDocId);
    const sourceUrl = doc ? buildCfpSessionDocumentUrl(doc.document) : null;
    if (!sourceUrl) {
      setPreviewBlobUrl(null);
      setPreviewStatus('idle');
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = null;
    setPreviewStatus('loading');
    setPreviewBlobUrl(null);

    (async () => {
      try {
        const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN);
        const response = await fetch(sourceUrl, {
          signal: controller.signal,
          referrerPolicy: 'no-referrer',
          cache: 'no-store',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          if (!controller.signal.aborted) setPreviewStatus('error');
          return;
        }
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        if (controller.signal.aborted) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewBlobUrl(objectUrl);
        setPreviewStatus('ready');
      } catch (error) {
        if (error?.name === 'AbortError') return;
        setPreviewStatus('error');
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documents, previewDocId]);

  useEffect(() => {
    if (!session) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (deleteConfirmDoc && !deletingId) {
        setDeleteConfirmDoc(null);
        return;
      }
      if (!submitting && !deletingId && !updatingId) {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [deleteConfirmDoc, deletingId, onClose, session, submitting, updatingId]);

  const resetCreateForm = () => {
    setTitle('');
    setSelectedFile(null);
    setFormError('');
    setFileInputKey((prev) => prev + 1);
  };

  const resetEditForm = () => {
    setEditingDoc(null);
    setEditTitle('');
    setEditFile(null);
    setFormError('');
    setEditFileInputKey((prev) => prev + 1);
  };

  const handleCreateFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    const validationError = file ? validateCfpSessionPdfFile(file) : null;
    setFormError(validationError || '');
    setSelectedFile(validationError ? null : file);
  };

  const handleEditFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      setEditFile(null);
      return;
    }
    const validationError = validateCfpSessionPdfFile(file);
    setFormError(validationError || '');
    setEditFile(validationError ? null : file);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const titleTrim = String(title ?? '').trim();
    if (!titleTrim) {
      setFormError('Title is required.');
      return;
    }
    const fileError = validateCfpSessionPdfFile(selectedFile);
    if (fileError) {
      setFormError(fileError);
      return;
    }

    setSubmitting(true);
    setFormError('');
    const result = await createUserCfpSessionDocument(
      { cfpSessionId: sessionId, title: titleTrim, documentFile: selectedFile },
      navigate,
    );

    if (result.ok) {
      resetCreateForm();
      setPreviewDocId(null);
      await loadDocuments();
      showFeedback({ type: 'success', message: result.message || 'Document uploaded successfully.' });
    } else {
      setFormError(result.message || 'Failed to upload document.');
    }
    setSubmitting(false);
  };

  const startEdit = (doc) => {
    setEditingDoc(doc);
    setEditTitle(String(doc?.title ?? ''));
    setEditFile(null);
    setFormError('');
    setEditFileInputKey((prev) => prev + 1);
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!editingDoc?.id) return;

    const titleTrim = String(editTitle ?? '').trim();
    const titleChanged = titleTrim !== String(editingDoc.title ?? '').trim();
    const hasNewFile = Boolean(editFile);

    if (!titleChanged && !hasNewFile) {
      setFormError('Change the title or select a new PDF to update.');
      return;
    }
    if (titleChanged && !titleTrim) {
      setFormError('Title is required.');
      return;
    }
    if (hasNewFile) {
      const fileError = validateCfpSessionPdfFile(editFile);
      if (fileError) {
        setFormError(fileError);
        return;
      }
    }

    setUpdatingId(editingDoc.id);
    setFormError('');
    const result = await updateUserCfpSessionDocument(
      {
        id: editingDoc.id,
        title: titleChanged ? titleTrim : undefined,
        documentFile: editFile,
      },
      navigate,
    );

    if (result.ok) {
      resetEditForm();
      await loadDocuments();
      showFeedback({ type: 'success', message: result.message || 'Document updated successfully.' });
    } else {
      setFormError(result.message || 'Failed to update document.');
    }
    setUpdatingId(null);
  };

  const openDeleteConfirm = (doc) => {
    if (!doc?.id || deletingId) return;
    setDeleteConfirmDoc(doc);
  };

  const closeDeleteConfirm = () => {
    if (deletingId) return;
    setDeleteConfirmDoc(null);
  };

  const confirmDelete = async () => {
    const doc = deleteConfirmDoc;
    if (!doc?.id || deletingId) return;

    setDeletingId(doc.id);
    const result = await deleteUserCfpSessionDocument({ id: doc.id }, navigate);

    if (result.ok) {
      setDeleteConfirmDoc(null);
      if (editingDoc?.id === doc.id) resetEditForm();
      if (previewDocId === doc.id) setPreviewDocId(null);
      await loadDocuments();
      showFeedback({ type: 'success', message: result.message || 'Document deleted successfully.' });
    } else {
      showFeedback({ type: 'error', message: result.message || 'Failed to delete document.' });
    }
    setDeletingId(null);
  };

  if (typeof document === 'undefined') return null;

  const dialogBusy = !canManageDocuments
    ? Boolean(isBusy)
    : submitting || Boolean(deletingId) || Boolean(updatingId) || isBusy;

  const previewDoc = documents.find((d) => d.id === previewDocId) ?? null;
  const previewUrl = previewDoc ? buildCfpSessionDocumentUrl(previewDoc.document) : null;

  return (
    <>
      <SuccessToast message={successToastMessage} isExiting={successToastExiting} />
      {createPortal(
    <AnimatePresence mode="wait">
      {session && (
        <motion.div
          key="cfp-documents-overlay"
          className={styles.listDocumentsOverlay}
          role="presentation"
          {...overlayMotion}
        >
          <motion.div
            className={styles.listDocumentsDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cfp-documents-dialog-title"
            onClick={(e) => e.stopPropagation()}
            {...cardMotion}
          >
            <button
              type="button"
              className={styles.listDocumentsDialogClose}
              onClick={onClose}
              disabled={dialogBusy}
              aria-label="Close dialog"
            >
              <CloseIcon />
            </button>

            <div className={styles.listDocumentsDialogHeader}>
              <div className={styles.listDocumentsDialogIconWrap} aria-hidden="true">
                <DocumentIcon />
              </div>
              <div>
                <h2 id="cfp-documents-dialog-title" className={styles.listDocumentsDialogTitle}>
                  Session Documents
                </h2>
                <p className={styles.listDocumentsDialogSubtitle} title={sessionTopic}>
                  {sessionTopic}
                </p>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {feedback && (
                <DocumentsFeedbackBanner
                  key={`${feedback.type}-${feedback.message}`}
                  feedback={feedback}
                  onDismiss={dismissFeedback}
                />
              )}
            </AnimatePresence>

            {documentsDisabledReason && (
              <p className={styles.listDocumentsReadOnlyNotice} role="status">
                {documentsDisabledReason}
              </p>
            )}

            {/* Two-column panel */}
            <div className={styles.listDocumentsDialogPanel}>

              {/* Left panel: document list + form */}
              <div className={styles.listDocumentsLeftPanel}>
                <div className={styles.listDocumentsDialogBody}>

                  <section className={styles.listDocumentsSection} aria-label="Uploaded documents">
                    <h3 className={styles.listDocumentsSectionTitle}>
                      {readOnly ? 'Documents' : 'Your documents'}
                    </h3>
                    {loading ? (
                      <div className={styles.listDocumentsLoading} aria-busy="true">
                        <span className={styles.listDocumentsSpinner} aria-hidden="true">
                          <SpinnerIcon />
                        </span>
                        Loading documents…
                      </div>
                    ) : documents.length === 0 ? (
                      <p className={styles.listDocumentsEmpty}>
                        {readOnly || !canManageDocuments
                          ? 'No documents uploaded for this session.'
                          : 'No documents yet. Add your first PDF below.'}
                      </p>
                    ) : (
                      <ul className={styles.listDocumentsList}>
                        <AnimatePresence initial={false}>
                          {documents.map((doc) => {
                            const docUrl = buildCfpSessionDocumentUrl(doc.document);
                            const isDeleting = canManageDocuments && deletingId === doc.id;
                            const isEditing = canManageDocuments && editingDoc?.id === doc.id;
                            const isPreviewing = previewDocId === doc.id;
                            const isPendingDelete = canManageDocuments && deleteConfirmDoc?.id === doc.id;
                            const anyPendingDelete = canManageDocuments && Boolean(deleteConfirmDoc);

                            return (
                              <motion.li
                                key={doc.id}
                                layout
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8, scale: 0.97 }}
                                transition={{ duration: 0.2, ease: dialogEase }}
                                className={`${styles.listDocumentsItem} ${isPreviewing ? styles.listDocumentsItemActive : ''} ${isPendingDelete ? styles.listDocumentsItemDeletePending : ''}`}
                              >
                                <AnimatePresence mode="wait" initial={false}>
                                  {isPendingDelete ? (
                                    <motion.div
                                      key="confirm"
                                      className={styles.listDocumentsDeleteConfirm}
                                      initial={{ opacity: 0, y: 6 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: -6 }}
                                      transition={{ duration: 0.18, ease: dialogEase }}
                                    >
                                      <p className={styles.listDocumentsDeleteConfirmText}>
                                        Delete <strong>{doc.title}</strong>? This cannot be undone.
                                      </p>
                                      <div className={styles.listDocumentsDeleteConfirmActions}>
                                        <button
                                          type="button"
                                          className={styles.listDocumentsDeleteConfirmNo}
                                          onClick={closeDeleteConfirm}
                                          disabled={isDeleting}
                                        >
                                          No
                                        </button>
                                        <button
                                          type="button"
                                          className={styles.listDocumentsDeleteConfirmYes}
                                          onClick={confirmDelete}
                                          disabled={isDeleting}
                                          aria-busy={isDeleting}
                                        >
                                          {isDeleting ? (
                                            <>
                                              <span className={styles.listDocumentsSpinner} aria-hidden="true">
                                                <SpinnerIcon />
                                              </span>
                                              Deleting…
                                            </>
                                          ) : (
                                            'Yes, delete'
                                          )}
                                        </button>
                                      </div>
                                    </motion.div>
                                  ) : (
                                    <motion.div
                                      key="row"
                                      className={styles.listDocumentsItemRow}
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      transition={{ duration: 0.15, ease: dialogEase }}
                                    >
                                      <div className={styles.listDocumentsItemMain}>
                                        <span className={styles.listDocumentsItemTitle} title={doc.title}>
                                          {doc.title}
                                        </span>
                                        {isEditing && (
                                          <span className={styles.listDocumentsItemEditingBadge}>Editing</span>
                                        )}
                                      </div>
                                      <div className={styles.listDocumentsItemActions}>
                                        {docUrl && (
                                          <button
                                            type="button"
                                            className={`${styles.listDocumentsPreviewButton} ${isPreviewing ? styles.listDocumentsPreviewButtonActive : ''}`}
                                            onClick={() => setPreviewDocId(isPreviewing ? null : doc.id)}
                                            disabled={dialogBusy || anyPendingDelete}
                                            aria-label={isPreviewing ? 'Hide PDF preview' : `Preview PDF: ${doc.title}`}
                                            title={isPreviewing ? 'Hide preview' : 'Preview PDF'}
                                          >
                                            <EyeIcon />
                                          </button>
                                        )}
                                        {canManageDocuments && (
                                          <>
                                            <button
                                              type="button"
                                              className={styles.listDocumentsEditButton}
                                              onClick={() => startEdit(doc)}
                                              disabled={dialogBusy || isEditing || anyPendingDelete}
                                              aria-label={`Edit ${doc.title}`}
                                            >
                                              <EditIcon />
                                            </button>
                                            <button
                                              type="button"
                                              className={styles.listDocumentsDeleteButton}
                                              onClick={() => openDeleteConfirm(doc)}
                                              disabled={dialogBusy || anyPendingDelete}
                                              aria-label={`Delete ${doc.title}`}
                                            >
                                              <DeleteIcon />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.li>
                            );
                          })}
                        </AnimatePresence>
                      </ul>
                    )}
                  </section>

                  {canManageDocuments && (
                    <AnimatePresence mode="wait" initial={false}>
                      {editingDoc ? (
                      <motion.section
                        key="edit-form"
                        className={styles.listDocumentsSection}
                        aria-label="Edit document"
                        {...sectionMotion}
                      >
                        <h3 className={styles.listDocumentsSectionTitle}>Edit document</h3>
                        <form className={styles.listDocumentsForm} onSubmit={handleUpdate}>
                          <label className={styles.listDocumentsField}>
                            <span className={styles.listDocumentsFieldLabel}>Title *</span>
                            <input
                              type="text"
                              className={styles.listDocumentsInput}
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              disabled={dialogBusy}
                              required
                            />
                          </label>
                          <label className={styles.listDocumentsField}>
                            <span className={styles.listDocumentsFieldLabel}>
                              Replace PDF (optional, max {config.MAX_CFP_SESSION_DOCUMENT_SIZE_MB} MB)
                            </span>
                            <input
                              key={editFileInputKey}
                              type="file"
                              accept="application/pdf,.pdf"
                              className={styles.listDocumentsFileInput}
                              onChange={handleEditFileChange}
                              disabled={dialogBusy}
                            />
                          </label>
                          {formError && (
                            <p className={styles.listDocumentsFormError} role="alert">
                              {formError}
                            </p>
                          )}
                          <div className={styles.listDocumentsFormActions}>
                            <button
                              type="button"
                              className={styles.listDocumentsCancelButton}
                              onClick={resetEditForm}
                              disabled={dialogBusy}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className={styles.listDocumentsSubmitButton}
                              disabled={dialogBusy}
                              aria-busy={Boolean(updatingId)}
                            >
                              {updatingId ? (
                                <>
                                  <span className={styles.listDocumentsSpinner} aria-hidden="true">
                                    <SpinnerIcon />
                                  </span>
                                  Updating…
                                </>
                              ) : (
                                'Save changes'
                              )}
                            </button>
                          </div>
                        </form>
                      </motion.section>
                    ) : (
                      <motion.section
                        key="add-form"
                        className={styles.listDocumentsSection}
                        aria-label="Add document"
                        {...sectionMotion}
                      >
                        <h3 className={styles.listDocumentsSectionTitle}>Add document</h3>
                        <form className={styles.listDocumentsForm} onSubmit={handleCreate}>
                          <label className={styles.listDocumentsField}>
                            <span className={styles.listDocumentsFieldLabel}>Title *</span>
                            <input
                              type="text"
                              className={styles.listDocumentsInput}
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                              disabled={dialogBusy}
                              required
                            />
                          </label>
                          <label className={styles.listDocumentsField}>
                            <span className={styles.listDocumentsFieldLabel}>
                              PDF file * (max {config.MAX_CFP_SESSION_DOCUMENT_SIZE_MB} MB)
                            </span>
                            <input
                              key={fileInputKey}
                              type="file"
                              accept="application/pdf,.pdf"
                              className={styles.listDocumentsFileInput}
                              onChange={handleCreateFileChange}
                              disabled={dialogBusy}
                              required
                            />
                          </label>
                          {formError && (
                            <p className={styles.listDocumentsFormError} role="alert">
                              {formError}
                            </p>
                          )}
                          <button
                            type="submit"
                            className={styles.listDocumentsSubmitButton}
                            disabled={dialogBusy}
                            aria-busy={submitting}
                          >
                            {submitting ? (
                              <>
                                <span className={styles.listDocumentsSpinner} aria-hidden="true">
                                  <SpinnerIcon />
                                </span>
                                Uploading…
                              </>
                            ) : (
                              'Upload document'
                            )}
                          </button>
                        </form>
                      </motion.section>
                    )}
                  </AnimatePresence>
                  )}

                </div>
              </div>

              {/* Right panel: PDF preview */}
              <div className={styles.listDocumentsRightPanel}>
                <AnimatePresence mode="wait">
                  {previewUrl ? (
                    <motion.div
                      key={`pdf-${previewDocId}`}
                      className={styles.listDocumentsPdfWrap}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18, ease: dialogEase }}
                    >
                      {previewBlobUrl ? (
                        <iframe
                          className={styles.listDocumentsPdfIframe}
                          src={previewBlobUrl}
                          title={`PDF: ${previewDoc.title}`}
                        />
                      ) : (
                        <div className={styles.listDocumentsPdfEmpty}>
                          {previewStatus === 'error' ? 'Unable to preview this PDF.' : 'Loading preview…'}
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="pdf-empty"
                      className={styles.listDocumentsPdfEmpty}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18, ease: dialogEase }}
                    >
                      <PdfPlaceholderIllustration />
                      <p className={styles.listDocumentsPdfEmptyTitle}>PDF Preview</p>
                      <p className={styles.listDocumentsPdfEmptyText}>
                        {documents.length > 0
                          ? 'Click the eye icon on a document to preview its PDF here.'
                          : readOnly || !canManageDocuments
                            ? 'No PDF documents are available to preview.'
                            : 'Upload a PDF document to see its preview here.'}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

            </div>
          </motion.div>
      </motion.div>
      )}
    </AnimatePresence>,
    document.body,
      )}
    </>
  );
}

export default CfpSessionDocumentsDialog;
